/**
 * AnalyticsDashboard - Enhanced Analytics Dashboard
 * 
 * Comprehensive user engagement analytics with:
 * - Activity Trends Over Time
 * - User Engagement Metrics
 * - Cohort Analysis
 * 
 * Access: Admin and SuperAdmin only
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import ActivityTrendChart from '../components/charts/ActivityTrendChart';
import ComparisonMetrics from '../components/analytics/ComparisonMetrics';
import EngagementMetrics from '../components/analytics/EngagementMetrics';
import FeatureUsageChart from '../components/charts/FeatureUsageChart';
import EngagementScoreHistogram from '../components/charts/EngagementScoreHistogram';
import LowEngagementUsersList from '../components/analytics/LowEngagementUsersList';
import CohortAnalysis from '../components/analytics/CohortAnalysis';
import { useActivityTrends } from '../hooks/useAnalyticsData';
import { useEngagementMetrics } from '../hooks/useEngagementMetrics';
import { useCohortData } from '../hooks/useCohortData';
import { API_ENDPOINTS } from '../config/api-endpoints';
import './AnalyticsDashboard.css';

const { DashboardSkeleton } = SkeletonLoaderComponents;

const AnalyticsDashboard = () => {
  const { user } = useAdminAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState('30d');
  const [engagementPeriod, setEngagementPeriod] = useState('30d');
  const [engagementTier, setEngagementTier] = useState('all');
  const [cohortGroupBy, setCohortGroupBy] = useState('month');
  const [cohortTier, setCohortTier] = useState('all');

  // Fetch activity trends data using custom hook
  const { 
    data: trendData, 
    loading: trendLoading, 
    error: trendError
  } = useActivityTrends(trendPeriod);

  // Fetch engagement metrics data using custom hook
  const {
    data: engagementData,
    loading: engagementLoading,
    error: engagementError
  } = useEngagementMetrics(engagementPeriod, engagementTier);

  // Fetch cohort data using custom hook
  const {
    cohorts,
    averageRetention,
    bestCohort,
    worstCohort,
    churnWarnings,
    loading: cohortLoading,
    error: cohortError
  } = useCohortData(cohortGroupBy, cohortTier);

  // Access control: Admin and SuperAdmin only
  const hasAccess = user?.groups?.includes('Admin') || user?.groups?.includes('SuperAdmin') || user?.isSuperAdmin;

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.ANALYTICS}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Analytics fetch failed: ${response.status}`);
      }
      const data = await response.json();
      setAnalytics(data.analytics || data || {});
    } catch (err) {
      console.error('[AnalyticsDashboard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle period change for trend chart
  const handlePeriodChange = (newPeriod, customDates) => {
    setTrendPeriod(newPeriod);
    // If custom dates provided, the hook will handle them
    if (customDates) {
      // Custom date handling is managed by useActivityTrends hook
      console.log('[AnalyticsDashboard] Custom date range:', customDates);
    }
  };

  // Handle engagement period change
  const handleEngagementPeriodChange = (newPeriod) => {
    setEngagementPeriod(newPeriod);
  };

  // Handle engagement tier change
  const handleEngagementTierChange = (newTier) => {
    setEngagementTier(newTier);
  };

  // Handle cohort groupBy change
  const handleCohortGroupByChange = (newGroupBy) => {
    setCohortGroupBy(newGroupBy);
  };

  // Handle cohort tier change
  const handleCohortTierChange = (newTier) => {
    setCohortTier(newTier);
  };

  useEffect(() => {
    if (hasAccess) {
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [fetchAnalytics, hasAccess]);

  // Access denied state
  if (!hasAccess) {
    return (
      <div className="admin-card">
        <div className="admin-alert admin-alert--error">
          <span>🔒</span>
          <div>
            <strong>Access Denied</strong>
            <p>You do not have permission to view analytics. This page is restricted to Admin and SuperAdmin users.</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !analytics) {
    return <DashboardSkeleton />;
  }

  return (
    <ErrorBoundary>
      <div className="analytics-dashboard">
        {/* Page Header */}
        <div className="analytics-dashboard__header">
          <h2 className="analytics-dashboard__title">Analytics Dashboard</h2>
          <p className="analytics-dashboard__description">
            Comprehensive user engagement analytics and insights
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="admin-alert admin-alert--warning">
            <span>⚠️</span>
            <div>
              <strong>Error Loading Analytics</strong>
              <p>{error}</p>
            </div>
            <div className="admin-btn-group">
              <button className="admin-btn admin-btn--sm" onClick={fetchAnalytics}>
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Section 1: Activity Trends Over Time */}
        <ErrorBoundary>
          <section className="analytics-section">
            <div className="analytics-section__header">
              <h3 className="analytics-section__title">Activity Trends Over Time</h3>
              <p className="analytics-section__description">
                Track user activity patterns and growth trends over time
              </p>
            </div>

            {/* Comparison Metrics */}
            {trendData?.comparison && (
              <ComparisonMetrics 
                comparison={trendData.comparison} 
                loading={trendLoading}
              />
            )}

            <div className="admin-card">
              <ActivityTrendChart
                data={trendData}
                period={trendPeriod}
                onPeriodChange={handlePeriodChange}
                loading={trendLoading}
                error={trendError?.message || (trendData?.dates?.length === 0 ? 'No trend data available for the selected period. Historical data will appear here once activity snapshots are collected.' : null)}
                height={400}
              />
            </div>
          </section>
        </ErrorBoundary>

        {/* Section 2: User Engagement Metrics */}
        <ErrorBoundary>
          <section className="analytics-section">
            <div className="analytics-section__header">
              <h3 className="analytics-section__title">User Engagement</h3>
              <p className="analytics-section__description">
                Detailed metrics on user interaction, session behavior, and feature usage patterns
              </p>
            </div>

            {/* Engagement Metrics Cards */}
            <EngagementMetrics
              metrics={engagementData?.metrics}
              period={engagementPeriod}
              tier={engagementTier}
              onPeriodChange={handleEngagementPeriodChange}
              onTierChange={handleEngagementTierChange}
              loading={engagementLoading}
            />

            {/* Feature Usage Breakdown */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h4 className="admin-card-title">Feature Usage (Top 10)</h4>
                <p className="admin-card-description">
                  Most frequently used features across the platform
                </p>
              </div>
              <FeatureUsageChart
                data={engagementData?.featureUsage || []}
                loading={engagementLoading}
                error={engagementError?.message}
              />
            </div>

            {/* Engagement Score Distribution */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h4 className="admin-card-title">Engagement Score Distribution</h4>
                <p className="admin-card-description">
                  Distribution of user engagement scores across the user base
                </p>
              </div>
              <EngagementScoreHistogram
                data={engagementData?.engagementScoreDistribution}
                loading={engagementLoading}
                error={engagementError?.message}
              />
            </div>

            {/* Low Engagement Users */}
            <div className="admin-card">
              <div className="admin-card-header">
                <h4 className="admin-card-title">Low Engagement Users (Score &lt; 0.3)</h4>
                <p className="admin-card-description">
                  Users who may benefit from outreach or additional support
                </p>
              </div>
              <LowEngagementUsersList
                users={engagementData?.lowEngagementUsers || []}
                loading={engagementLoading}
                error={engagementError?.message}
              />
            </div>
          </section>
        </ErrorBoundary>

        {/* Section 3: Cohort Analysis */}
        <ErrorBoundary>
          <section className="analytics-section">
            <CohortAnalysis
              cohorts={cohorts}
              averageRetention={averageRetention}
              bestCohort={bestCohort}
              worstCohort={worstCohort}
              churnWarnings={churnWarnings}
              loading={cohortLoading}
              groupBy={cohortGroupBy}
              tier={cohortTier}
              onGroupByChange={handleCohortGroupByChange}
              onTierChange={handleCohortTierChange}
            />
          </section>
        </ErrorBoundary>

        {/* Footer Note */}
        <div className="analytics-dashboard__footer">
          <p className="analytics-dashboard__note">
            📊 Analytics system includes activity trends, engagement metrics, and cohort retention analysis. More features coming soon!
          </p>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AnalyticsDashboard;
